// ============================================================================
// Composite Scoring Engine — Spec 2026-05-09
// ============================================================================
// Output: integer 1-100 score per kandidat
//
// BASE (sum to 100):
//   biometric_512dim_cosine × 40
//   name_match              × 25
//   username_handle_match   × 15
//   location_match          × 10
//   cross_platform_consist  × 10
//                            ──
//                           100
//
// BONUS (additive, total cap 100):
//   NIK ditemukan di source manapun  → +15
//   Keywords match di source         → +8 per keyword (cap +16)
//   Tempat kerja match di source     → +8
//   Lokasi terkini match di source   → +5
//
// Tier:
//   HIGH      80-100  → grid 6 tile, AUTO badge if score ≥85 + name_match strong
//   MEDIUM    50-79   → grid max 20 tile
//   LOW       30-49   → list collapsed
//   DISCARDED  <30    → hidden
// ============================================================================

import { lookupProvinsi, corpusMentionsProvince, corpusMentionsSiblingCity } from './provinceMap.js';

const W_BIOMETRIC = 40;
const W_NAME      = 25;
const W_HANDLE    = 15;
const W_LOCATION  = 10;
const W_CROSS     = 10;

const BONUS_NIK         = 15;
const BONUS_KEYWORD     = 8;
const BONUS_KEYWORD_CAP = 16;
const BONUS_EMPLOYER    = 8;
const BONUS_LOCATION    = 5;

/**
 * Pure Levenshtein similarity (0..1) — single-string distance ratio.
 * 1 = exact match, 0 = totally different. Tidak ada token logic.
 * Dipakai pure form untuk ALIAS detection per spec 4.1 ("levenshtein < 0.30").
 */
function levenshteinSimilarity(a, b) {
  if (!a || !b) return 0;
  const s1 = String(a).toLowerCase().trim();
  const s2 = String(b).toLowerCase().trim();
  if (s1 === s2) return 1;
  const m = s1.length, n = s2.length;
  if (m === 0 || n === 0) return 0;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++) {
    dp[i][j] = s1[i - 1] === s2[j - 1] ? dp[i - 1][j - 1]
      : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  }
  return 1 - dp[m][n] / Math.max(m, n);
}

/**
 * Token Sort Ratio (fuzzywuzzy-style) — sort token alphabetically lalu
 * compare via levenshtein. Handle nama terbalik dgn benar:
 *   "Andra Sandi" vs "Sandi Andra" → both → "andra sandi" → sim 1.0
 * Per spec 3.3 dipakai untuk Name match (token_sort + levenshtein) / 2.
 */
function tokenSortRatio(a, b) {
  if (!a || !b) return 0;
  const sort = (s) => String(s).toLowerCase().trim()
    .split(/\s+/).filter(t => t.length > 0).sort().join(' ');
  return levenshteinSimilarity(sort(a), sort(b));
}

/**
 * Fuzzy fuzzymatch — composite hybrid (intersection token + levenshtein, take max).
 * Dipakai untuk username/handle match (spec 3.2 — "exact OR partial match").
 * NAME match TIDAK pakai ini lagi — pakai nameSimilarity per spec 3.3.
 */
function fuzzyMatch(a, b) {
  if (!a || !b) return 0;
  const s1 = String(a).toLowerCase().trim();
  const s2 = String(b).toLowerCase().trim();
  if (s1 === s2) return 1;
  if (s1.includes(s2) || s2.includes(s1)) {
    const ratio = Math.min(s1.length, s2.length) / Math.max(s1.length, s2.length);
    return 0.6 + ratio * 0.4;
  }
  const t1 = new Set(s1.split(/\s+/).filter(t => t.length >= 2));
  const t2 = new Set(s2.split(/\s+/).filter(t => t.length >= 2));
  let tokenScore = 0;
  if (t1.size > 0 && t2.size > 0) {
    let matches = 0;
    for (const tok of t1) if (t2.has(tok)) matches++;
    tokenScore = matches / Math.max(t1.size, t2.size);
  }
  return Math.max(tokenScore, levenshteinSimilarity(s1, s2));
}

/**
 * Name similarity per spec 3.3: (token_sort_ratio + levenshtein_similarity) / 2
 * Token sort handle "Andra Sandi" vs "Sandi Andra" → 1.0
 * Levenshtein handle typo "Andra Sandi" vs "Andra Sndi" → ~0.9
 * Average → balanced — bukan over-aggressive (max) bukan over-conservative (min).
 *
 * Return object dgn breakdown supaya caller bisa pakai komponen levenshtein
 * pure untuk ALIAS detection per spec 4.1.
 */
function nameSimilarity(a, b) {
  if (!a || !b) return { combined: 0, token_sort: 0, levenshtein: 0 };
  const tokenSort   = tokenSortRatio(a, b);
  const levSim      = levenshteinSimilarity(a, b);
  const combined    = (tokenSort + levSim) / 2;
  return { combined, token_sort: tokenSort, levenshtein: levSim };
}

/**
 * Substring contains check (case-insensitive). Returns true kalau needle ada di haystack.
 * Dipakai untuk NIK match (exact digits) + keywords (substring sudah cukup).
 */
function containsCi(haystack, needle) {
  if (!haystack || !needle) return false;
  return String(haystack).toLowerCase().includes(String(needle).toLowerCase());
}

/**
 * Fuzzy contains — true kalau needle ada di corpus dengan similarity >= threshold.
 * Multi-strategy:
 *  1) Exact substring → langsung true
 *  2) Per-token corpus check — tiap token corpus dibandingkan levenshtein vs needle
 *  3) Multi-word needle — semua token needle harus punya match >=threshold di corpus
 * Per spec 3.4 dipakai untuk Tempat Kerja match (threshold 0.75).
 */
function fuzzyContains(corpus, needle, threshold = 0.75) {
  if (!corpus || !needle) return false;
  const c = String(corpus).toLowerCase();
  const n = String(needle).toLowerCase().trim();
  if (n.length === 0) return false;
  if (c.includes(n)) return true;
  if (n.length < 3) return false;
  const corpusTokens = c.split(/[\s,.;:|/\\()[\]{}"'!?<>=+]+/).filter(t => t.length >= 3);
  for (const tok of corpusTokens) {
    if (levenshteinSimilarity(tok, n) >= threshold) return true;
  }
  const needleTokens = n.split(/\s+/).filter(t => t.length >= 3);
  if (needleTokens.length > 1) {
    let allMatch = true;
    for (const nt of needleTokens) {
      let best = 0;
      for (const ct of corpusTokens) {
        const sim = levenshteinSimilarity(nt, ct);
        if (sim > best) { best = sim; if (best >= threshold) break; }
      }
      if (best < threshold) { allMatch = false; break; }
    }
    if (allMatch) return true;
  }
  return false;
}

// Kota/kabupaten → provinsi mapping di provinceMap.js — cover semua 38 provinsi
// Indonesia + 500+ kabupaten/kota + aliases per provinsi (KALTIM, JABAR, NTT, dll).
// Import lookupProvinsi, corpusMentionsProvince, corpusMentionsSiblingCity di
// top of file.

/**
 * Concat semua text source dari kandidat per Google Docs Section 3.4.
 * Bonus additive checks ALL sources: bio, posts, articles/news, forums, PDFs,
 * Apify enriched data, dorking SERP snippets. Keyword di Detik.com article =
 * keyword di Instagram bio (transparency principle: source-agnostic).
 */
function candidateTextCorpus(candidate) {
  const parts = [
    // Profile primary
    candidate.bio || candidate.biography || '',
    candidate.title || '',
    candidate.snippet || candidate.description || '',
    candidate.display_name || '',
    candidate.handle || candidate.username || '',
    candidate.full_name || '',
    // Posts (sosmed)
    Array.isArray(candidate.posts) ? candidate.posts.map(p => p.text || p.caption || '').join(' ') : '',
    Array.isArray(candidate.tweets) ? candidate.tweets.map(t => t.text || '').join(' ') : '',
    // Articles / news / forum / PDFs (text-based sources from dorking)
    Array.isArray(candidate.articles) ? candidate.articles.map(a => `${a.title || ''} ${a.snippet || a.text || ''}`).join(' ') : '',
    Array.isArray(candidate.forums) ? candidate.forums.map(f => `${f.title || ''} ${f.text || ''}`).join(' ') : '',
    Array.isArray(candidate.pdfs) ? candidate.pdfs.map(p => `${p.title || ''} ${p.text || ''}`).join(' ') : '',
    // Apify enriched data dump
    candidate.apifyData ? JSON.stringify(candidate.apifyData).slice(0, 5000) : '',
    // Dorking SERP snippets (raw search results context)
    Array.isArray(candidate.dorkingSnippets) ? candidate.dorkingSnippets.join(' ') : '',
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

/**
 * Hitung composite score untuk 1 kandidat per Google Docs Section 3.2-3.5.
 *
 * @param {object} subject  - { nik, nama_lengkap, kota_kab_ktp, foto_url, biometric_score? }
 * @param {object} candidate - { handle, display_name, bio, snippet, photo_match (0-1), platform, posts[]? }
 * @param {object} hints   - { username, lokasiTerkini, tempatKerja, keywords[] }
 * @param {object} crossPlatformContext - { handle_count_across_platforms, photo_match_count_across_platforms }
 * @param {object} biometricGateStatus - { available, scoreCap, warning } from checkBiometricGate()
 * @returns {object} { score (1-100), tier, breakdown, bonuses, auto_confirm, score_cap, biometric_gate_warning }
 */
export function computeCompositeScore(subject, candidate, hints = {}, crossPlatformContext = {}, biometricGateStatus = null) {
  const corpus = candidateTextCorpus(candidate);

  // ─── BASE SIGNALS ──────────────────────────────────────────────
  // 1. Biometric 512-dim cosine (highest weight × 40)
  const biometricScore = Math.max(0, Math.min(1,
    candidate.photo_match ?? candidate.similarity ?? candidate.biometric_score ?? 0
  ));
  const biometricPts = biometricScore * W_BIOMETRIC;

  // 2. Name match (× 25) per spec 3.3:
  //    nameScore = (token_sort_ratio + levenshtein_similarity) / 2
  //    Token sort handle "Andra Sandi" vs "Sandi Andra" (nama terbalik).
  //    Levenshtein handle typo. Average = balanced.
  const candidateName = candidate.display_name || candidate.full_name || candidate.handle || '';
  const nameSig    = nameSimilarity(subject.nama_lengkap, candidateName);
  const nameScore  = nameSig.combined;
  const namePts    = nameScore * W_NAME;

  // 3. Username handle match (× 15) — pakai fuzzyMatch (token+lev max).
  const handleScore = hints.username
    ? fuzzyMatch(hints.username.replace(/^@/, ''), (candidate.handle || candidate.username || '').replace(/^@/, ''))
    : 0;
  const handlePts = handleScore * W_HANDLE;

  // 4. Location match (× 10) per spec 3.2:
  //    effectiveLoc = lokasiTerkini || kota_kab_ktp
  //    Kota match (effectiveLoc name in corpus) → 1.0
  //    Else provinsi match → 0.5. Provinsi match terdeteksi via:
  //      (a) nama provinsi atau alias-nya (KALTIM, JATIM, NTT, dll) di corpus
  //      (b) ATAU kota LAIN dari provinsi yang sama di corpus
  //    Else → 0
  // Contoh: subjek Kutai Kartanegara, corpus "Samarinda" → both Kaltim → 0.5.
  // Contoh: subjek Bandung, corpus "🌴 Jabar" → alias match → 0.5.
  const subjectLoc    = subject.kota_kab_ktp || subject.kota_kabupaten || '';
  const lokasiTerkini = hints.lokasiTerkini || '';
  const effectiveLoc  = lokasiTerkini || subjectLoc;
  let locScore = 0;
  if (effectiveLoc && containsCi(corpus, effectiveLoc)) {
    locScore = 1.0;
  } else {
    const subjectProvinsi = lookupProvinsi(effectiveLoc);
    if (subjectProvinsi) {
      if (corpusMentionsProvince(corpus, subjectProvinsi)
          || corpusMentionsSiblingCity(corpus, subjectProvinsi)) {
        locScore = 0.5;
      }
    }
  }
  const locPts = locScore * W_LOCATION;

  // 5. Cross-platform consistency (× 10) — kandidat sama (handle/face) muncul di N platform
  const platformCount = Math.max(
    crossPlatformContext.handle_count_across_platforms || 0,
    crossPlatformContext.photo_match_count_across_platforms || 0,
  );
  // 1 platform = 0%, 2 platform = 50%, 3+ platform = 100%
  const crossScore = platformCount >= 3 ? 1.0 : platformCount === 2 ? 0.5 : 0;
  const crossPts = crossScore * W_CROSS;

  let baseScore = biometricPts + namePts + handlePts + locPts + crossPts;

  // ─── BONUSES (additive, cap 100) ───────────────────────────────
  const bonuses = { nik: 0, keywords: 0, employer: 0, location: 0 };

  // NIK found anywhere → +15
  if (subject.nik && containsCi(corpus, subject.nik)) {
    bonuses.nik = BONUS_NIK;
  }

  // Keywords match → +8 per keyword (cap +16)
  const kws = Array.isArray(hints.keywords) ? hints.keywords : [];
  if (kws.length > 0) {
    let matches = 0;
    for (const kw of kws) if (containsCi(corpus, kw)) matches++;
    bonuses.keywords = Math.min(BONUS_KEYWORD_CAP, matches * BONUS_KEYWORD);
  }

  // Tempat kerja match → +8 per spec 3.4: "fuzzy match threshold 0.75"
  // Lebih lenient dari substring — handle typo, abbreviation, variation
  // ("PT. Bank XYZ" vs "bank xyz", "Detik.com" vs "detik com").
  if (hints.tempatKerja && fuzzyContains(corpus, hints.tempatKerja, 0.75)) {
    bonuses.employer = BONUS_EMPLOYER;
  }

  // Lokasi terkini match → +5 — operator-provided current location (terpisah dari kotaKTP).
  // Per Google Docs Section 3.4 example: bonus only triggers kalau lokasiTerkini DIFFERENT
  // dari kotaKTP (otherwise sama dengan base location, tidak signal baru).
  const subjectKotaKTP = subject.kota_kab_ktp || subject.kota_kabupaten || '';
  if (hints.lokasiTerkini
      && hints.lokasiTerkini.toLowerCase().trim() !== subjectKotaKTP.toLowerCase().trim()
      && containsCi(corpus, hints.lokasiTerkini)) {
    bonuses.location = BONUS_LOCATION;
  }

  const totalBonus = bonuses.nik + bonuses.keywords + bonuses.employer + bonuses.location;

  // SCORE CAP — Biometric Gate per Google Docs Section 3.5:
  // final_score = min(base + bonus, score_cap). score_cap = 100 (biometric available)
  // atau 60 (biometric unavailable, max MEDIUM tier supaya operator tidak misled).
  const scoreCap = biometricGateStatus?.scoreCap ?? 100;
  let finalScore = Math.min(scoreCap, Math.round(baseScore + totalBonus));

  // USERNAME UNIK (ALIAS) detection per spec 4.1:
  //   (biometric_cosine ≥ 0.65) AND (levenshtein < 0.30)
  // Critical: pakai PURE levenshtein (bukan combined score) supaya "Andra Sandi"
  // vs "Sandi Andra" (token-swap → combined=1.0 tapi levenshtein=0.18) tidak
  // terklasifikasi ALIAS — token-swap = nama mirip, bukan alias.
  // True ALIAS case: "Andra Sandi" vs "John Smith" face=0.70 levenshtein=0.10 → ALIAS YES.
  const aliasDetected = biometricScore >= 0.65 && nameSig.levenshtein < 0.30;

  // ─── BIOMETRIC FLOOR — Addendum v3 2026-05-19 (kasus borneosonofficial 63%) ────
  // Spec KES v1.0 base: biometric weight 40, DISCARDED <30. Konsekuensi:
  // candidate dgn face MATCH (≥0.60) tapi nama/handle/lokasi nol = base ~25
  // → DISCARDED, padahal face evidence kuat. Misalnya YouTube channel
  // "borneosonofficial" 63% MATCH face Andra Sandi — composite ~28, hilang
  // dari panel.
  //
  // User feedback 2026-05-19 (3x reiteration): "foto foto yang memiliki
  // similarity tinggi harusnya masuk ke HIGH CONFIDENCE MATCHES (80-100)".
  // Strong biometric = strong evidence; itu jadi anchor, name/handle cuma
  // confirm. Operator masih harus verify HIGH bucket (tidak auto-confirmed
  // tanpa name match — AUTO badge tetap require namePts ≥ 17.5).
  //
  // Floor v3 (lebih agresif dari v2):
  //   biometric ≥ 0.80 (STRONG)  → floor 90 (HIGH AUTO-zone candidate)
  //   biometric ≥ 0.60 (MATCH)   → floor 80 (HIGH minimum)
  //   biometric ≥ 0.45 (POSSIBLE)→ floor 50 (MEDIUM minimum)
  //   alias_detected (≥0.65 + name<0.30) — flag badge USERNAME UNIK
  //
  // Properti yang dipreserve:
  //   - AUTO badge tetap require namePts ≥ 17.5 (HIGH tile tampil tapi
  //     tanpa ✓ AUTO badge — operator wajib verify)
  //   - scoreCap (60 saat biometric gate warn) tetap dipatuhi
  //   - Tidak menambah false positive: 0.60 InsightFace cosine = ~95%
  //     accuracy across pose/lighting (state-of-art threshold)
  let biometricFloorApplied = null;  // { threshold, floor, label } | null
  const BIOMETRIC_FLOORS = [
    { threshold: 0.80, floor: 90, label: 'strong' },    // STRONG → HIGH AUTO-zone
    { threshold: 0.60, floor: 80, label: 'match' },     // MATCH → HIGH minimum
    { threshold: 0.45, floor: 50, label: 'possible' },  // POSSIBLE → MEDIUM minimum
  ];
  for (const { threshold, floor, label } of BIOMETRIC_FLOORS) {
    if (biometricScore >= threshold && finalScore < floor) {
      finalScore = Math.min(scoreCap, floor);
      biometricFloorApplied = { threshold, floor, label };
      break;
    }
  }
  // Backward-compat alias_floor_applied — true kalau alias_detected DAN floor diterapkan
  const aliasFloorApplied = !!biometricFloorApplied && aliasDetected;

  // ─── TIER CLASSIFICATION ──────────────────────────────────────
  const tier = finalScore >= 80 ? 'HIGH'
             : finalScore >= 50 ? 'MEDIUM'
             : finalScore >= 30 ? 'LOW'
             : 'DISCARDED';

  // AUTO badge — double-gate per Google Docs Section 4.1:
  // (final_score ≥85) AND (name component score ≥17.5 out of 25 = 70%)
  // Double-gate cegah false positive dari biometric-only high score + name mismatch.
  // CATATAN: ALIAS floor TIDAK trigger AUTO karena namePts pasti rendah saat alias.
  const autoConfirm = finalScore >= 85 && namePts >= 17.5;

  // ─── DATA ELEVATED FLAGS per Google Docs Section 5.2 ──────────
  // Detect sensitive data: phone numbers (10-15 digits), emails, geo-tagged posts.
  const elevatedTypes = [];
  if (/\b\d{10,15}\b/.test(corpus) || /(\+?62|\b08)\d{8,12}\b/.test(corpus)) elevatedTypes.push('phone');
  if (/[\w.+-]+@[\w-]+\.[\w.-]+/.test(corpus)) elevatedTypes.push('email');
  if (Array.isArray(candidate.posts) && candidate.posts.some(p => p.location?.lat && p.location?.lng)) elevatedTypes.push('geo');
  const dataElevated = elevatedTypes.length > 0;

  return {
    score: finalScore,
    score_cap: scoreCap,
    biometric_gate_warning: biometricGateStatus?.warning || null,
    biometric_available: biometricGateStatus?.available !== false,
    tier,
    auto_confirm: autoConfirm,
    alias_detected: aliasDetected,           // Google Docs 4.1 USERNAME UNIK badge trigger
    alias_floor_applied: aliasFloorApplied,  // Addendum 2026-05-19: floor applied DAN alias_detected
    biometric_floor_applied: biometricFloorApplied,  // Addendum v2 2026-05-19: { threshold, floor, label } | null
    data_elevated: dataElevated,             // Google Docs 5.2 sensitive data flag
    elevated_types: elevatedTypes,           // ['phone','email','geo']
    breakdown: {
      biometric:  Math.round(biometricPts * 10) / 10,    // 0-40
      name:       Math.round(namePts * 10) / 10,         // 0-25
      handle:     Math.round(handlePts * 10) / 10,       // 0-15
      location:   Math.round(locPts * 10) / 10,          // 0-10
      cross:      Math.round(crossPts * 10) / 10,        // 0-10
      base_total: Math.round(baseScore * 10) / 10,       // 0-100
    },
    bonuses,
    raw_signals: {
      biometric_cosine: biometricScore,
      name_match:       nameScore,            // combined (token_sort + lev) / 2
      name_token_sort:  nameSig.token_sort,   // spec 3.3 component A
      name_levenshtein: nameSig.levenshtein,  // spec 3.3 component B + 4.1 ALIAS basis
      handle_match:     handleScore,
      location_score:   locScore,
      cross_score:      crossScore,
    },
  };
}

/**
 * Build cross-platform context dari array kandidat — count berapa platform punya
 * handle yang sama atau face yang match. Pre-compute sekali untuk semua kandidat.
 */
export function buildCrossPlatformContext(allCandidates) {
  // Group by handle (lowercase, normalized)
  const handleMap = new Map();
  for (const c of allCandidates) {
    const h = (c.handle || c.username || '').toLowerCase().replace(/[._\-\s]/g, '');
    if (!h) continue;
    if (!handleMap.has(h)) handleMap.set(h, new Set());
    handleMap.get(h).add(c.platform);
  }
  // Group by photo similarity ≥0.65 (rough cluster)
  // Simple: count kandidat dgn similarity ≥0.65 muncul di N platform
  const highMatchCandidates = allCandidates.filter(c => (c.photo_match ?? c.similarity ?? 0) >= 0.65);
  const photoPlatforms = new Set(highMatchCandidates.map(c => c.platform));

  return {
    // Per kandidat: berapa platform punya handle yang sama
    getContext: (candidate) => {
      const h = (candidate.handle || candidate.username || '').toLowerCase().replace(/[._\-\s]/g, '');
      const handleCount = handleMap.get(h)?.size || 1;
      const photoMatchCount = ((candidate.photo_match ?? candidate.similarity ?? 0) >= 0.65)
        ? photoPlatforms.size
        : 1;
      return {
        handle_count_across_platforms: handleCount,
        photo_match_count_across_platforms: photoMatchCount,
      };
    },
  };
}

/**
 * Tier color tokens untuk UI.
 */
export const TIER_COLORS = {
  HIGH:      'var(--color-cyan)',
  MEDIUM:    'var(--color-green)',
  LOW:       'var(--text-mute)',
  DISCARDED: 'transparent',
};

export const TIER_LABEL = {
  HIGH:      'HIGH CONFIDENCE',
  MEDIUM:    'MEDIUM CONFIDENCE',
  LOW:       'LOW CONFIDENCE',
  DISCARDED: 'DISCARDED',
};
